import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { QrCode, ArrowLeft, CheckCircle2, Scan, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PalmNFCIcon from '../components/icons/PalmNFCIcon';

const Palm2QR = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState('intro'); // intro, scanning, scanQR, verify, success
  const [amount, setAmount] = useState('');
  const [scanProgress, setScanProgress] = useState(0);
  const [merchantName, setMerchantName] = useState('Cafe Coffee Day');
  const [scannedQRData, setScannedQRData] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const canvasRef = useRef(null);
  const isScanningQRRef = useRef(false);

  useEffect(() => {
    if (step === 'scanning') {
      const interval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setStep('verify');
            return 100;
          }
          return prev + 5;
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [step]);

  const startScan = () => {
    setStep('scanning');
    setScanProgress(0);
  };

  const startQRScan = async () => {
    // Check if BarcodeDetector is supported
    if (!('BarcodeDetector' in window)) {
      toast({
        title: "QR Scanner Not Supported",
        description: "Your browser doesn't support QR code scanning. Please use Chrome or Edge browser.",
        variant: "destructive"
      });
      return;
    }

    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment' // Use back camera on mobile
        } 
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      
      setStep('scanQR');
      isScanningQRRef.current = true;
      
      // Start QR code detection
      startQRDetection();
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast({
        title: "Camera Access Denied",
        description: "Please allow camera access to scan QR codes",
        variant: "destructive"
      });
    }
  };

  const startQRDetection = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    const detectQR = async () => {
      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        try {
          // Try using BarcodeDetector API (Chrome/Edge)
          if ('BarcodeDetector' in window) {
            const barcodeDetector = new BarcodeDetector({ formats: ['qr_code'] });
            const barcodes = await barcodeDetector.detect(canvas);
            
            if (barcodes.length > 0) {
              const qrData = barcodes[0].rawValue;
              handleQRScanned(qrData);
              return;
            }
          }
          
          // Fallback: Use jsQR library if available, or manual detection
          // For now, we'll use a simple pattern matching approach
          // In production, you'd want to use a proper QR library like jsQR
        } catch (error) {
          console.error('QR detection error:', error);
        }
      }
      
      // Continue scanning
      if (isScanningQRRef.current) {
        scanIntervalRef.current = requestAnimationFrame(detectQR);
      }
    };

    video.addEventListener('loadedmetadata', () => {
      detectQR();
    });

    if (video.readyState >= 2) {
      detectQR();
    }
  };

  const handleQRScanned = (qrData) => {
    isScanningQRRef.current = false;
    
    // Stop camera stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (scanIntervalRef.current) {
      cancelAnimationFrame(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    setScannedQRData(qrData);
    
    // Parse QR data (assuming it contains merchant info)
    try {
      const qrInfo = JSON.parse(qrData);
      if (qrInfo.merchantName) {
        setMerchantName(qrInfo.merchantName);
      }
      if (qrInfo.amount) {
        setAmount(qrInfo.amount);
      }
    } catch (e) {
      // If QR data is not JSON, treat it as merchant identifier
      console.log('QR Data:', qrData);
    }

    toast({
      title: "QR Code Scanned",
      description: "QR code scanned successfully. Proceeding to payment...",
    });

    // Proceed to scanning step (palm scan)
    setTimeout(() => {
      setStep('scanning');
      setScanProgress(0);
    }, 1000);
  };

  const stopQRScan = () => {
    isScanningQRRef.current = false;
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (scanIntervalRef.current) {
      cancelAnimationFrame(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    setStep('intro');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (scanIntervalRef.current) {
        cancelAnimationFrame(scanIntervalRef.current);
      }
    };
  }, []);

  const confirmPayment = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Please enter a valid amount",
        variant: "destructive"
      });
      return;
    }

    setStep('success');
    setTimeout(() => {
      navigate('/home');
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0F1F] via-[#1a1f3a] to-[#0A0F1F] text-white">
      <style>{`
        @keyframes scanLine {
          0% { top: 20%; }
          50% { top: 80%; }
          100% { top: 20%; }
        }
      `}</style>
      {/* Intro */}
      {step === 'intro' && (
        <div className="min-h-screen flex flex-col px-6 py-12">
          <button onClick={() => navigate('/home')} className="self-start mb-6 p-2 hover:bg-white/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="relative mb-8">
              <div className="absolute inset-0 rounded-full bg-[#586BFF] blur-3xl opacity-40 animate-pulse"></div>
              <div className="relative w-32 h-32 bg-[#586BFF]/20 rounded-full flex items-center justify-center">
                <div className="relative">
                  <QrCode className="w-16 h-16 text-[#586BFF]" />
                  <PalmNFCIcon className="w-8 h-8 absolute -bottom-2 -right-2" style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(86%) saturate(3068%) hue-rotate(154deg) brightness(101%) contrast(101%)' }} />
                </div>
              </div>
            </div>

            <h1 className="text-3xl font-bold mb-4 text-center">Palm2QR Payment</h1>
            <p className="text-gray-300 text-center mb-8 max-w-md">
              Scan QR codes using your palm biometric for instant authentication. No PIN required.
            </p>

            <Card className="bg-[#1a1f3a]/80 border-[#586BFF]/30 p-6 mb-8 max-w-md">
              <h3 className="font-semibold text-white mb-4">How it works:</h3>
              <ol className="space-y-3 text-sm text-gray-300">
                <li className="flex gap-3">
                  <span className="text-[#586BFF] font-bold">1.</span>
                  <span>Show your palm to the camera</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#586BFF] font-bold">2.</span>
                  <span>Camera will scan the merchant QR code</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#586BFF] font-bold">3.</span>
                  <span>Triple-hash generated locally</span>
                </li>
                <li className="flex gap-3">
                  <span className="text-[#586BFF] font-bold">4.</span>
                  <span>Confirm amount and pay instantly</span>
                </li>
              </ol>
            </Card>

            <div className="w-full max-w-md space-y-3">
              <Button 
                onClick={startScan}
                className="w-full bg-gradient-to-r from-[#586BFF] to-[#9B62FF] hover:from-[#4a5ceb] hover:to-[#8a51eb] h-14 text-lg font-semibold"
              >
                Start Palm2QR Scan
              </Button>
              <Button 
                onClick={startQRScan}
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10 h-12 flex items-center justify-center gap-2"
              >
                <Scan className="w-5 h-5" />
                Scan QR
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* QR Scanning Step */}
      {step === 'scanQR' && (
        <div className="min-h-screen flex flex-col px-6 py-12">
          <button onClick={stopQRScan} className="self-start mb-6 p-2 hover:bg-white/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div className="flex-1 flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold mb-2">Scan QR Code</h2>
            <p className="text-gray-300 text-center mb-8 max-w-md">
              Point your camera at the merchant's QR code
            </p>

            <div className="relative w-full max-w-md mb-8">
              <div className="relative aspect-square bg-black rounded-2xl overflow-hidden border-2 border-[#586BFF]">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />
                
                {/* Scanning overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-64 h-64 border-2 border-[#64E8FF] rounded-lg relative">
                    {/* Corner indicators */}
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-[#64E8FF] rounded-tl-lg"></div>
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-[#64E8FF] rounded-tr-lg"></div>
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-[#64E8FF] rounded-bl-lg"></div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-[#64E8FF] rounded-br-lg"></div>
                  </div>
                </div>

                {/* Scanning line animation */}
                <div 
                  className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#64E8FF] to-transparent"
                  style={{
                    top: '50%',
                    animation: 'scanLine 2s linear infinite',
                  }}
                ></div>
              </div>
            </div>

            <p className="text-sm text-gray-400 text-center">
              Position the QR code within the frame
            </p>
          </div>
        </div>
      )}

      {/* Scanning */}
      {step === 'scanning' && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6">
          <h2 className="text-2xl font-bold mb-2">Scanning...</h2>
          <p className="text-gray-300 mb-8">Hold your palm steady over the QR code</p>

          <div className="relative w-80 h-80 mb-8">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0A0F1F] to-[#1a1f3a] rounded-3xl border-2 border-[#586BFF] overflow-hidden">
              {/* Palm + QR Overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <QrCode className="w-32 h-32 text-[#586BFF]/50" />
                  <PalmNFCIcon className="w-20 h-20 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(86%) saturate(3068%) hue-rotate(154deg) brightness(101%) contrast(101%)' }} />
                </div>
              </div>

              {/* Scan Line */}
              <div 
                className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#64E8FF] to-transparent shadow-lg shadow-[#64E8FF]"
                style={{ 
                  top: `${(scanProgress / 100) * 100}%`,
                  transition: 'top 0.1s linear'
                }}
              ></div>
            </div>
          </div>

          <div className="w-full max-w-md">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-400">Authenticating</span>
              <span className="text-[#64E8FF] font-semibold">{scanProgress}%</span>
            </div>
            <div className="h-2 bg-[#1a1f3a] rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-[#586BFF] to-[#64E8FF] transition-all"
                style={{ width: `${scanProgress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}

      {/* Verify Payment */}
      {step === 'verify' && (
        <div className="min-h-screen flex flex-col px-6 py-12">
          <button onClick={() => setStep('intro')} className="self-start mb-6 p-2 hover:bg-white/10 rounded-full transition-colors">
            <ArrowLeft className="w-6 h-6" />
          </button>

          <div className="flex-1 flex flex-col items-center justify-center">
            <h2 className="text-2xl font-bold mb-2">Confirm Payment</h2>
            <p className="text-gray-300 mb-8">Biometric authenticated successfully</p>

            <Card className="bg-[#1a1f3a]/80 border-[#586BFF]/30 p-6 mb-6 w-full max-w-md">
              <div className="text-center mb-6">
                <p className="text-gray-400 text-sm mb-2">Paying to</p>
                <p className="text-2xl font-bold text-white">{merchantName}</p>
              </div>

              <div className="mb-6">
                <label className="text-sm font-medium text-gray-400 mb-2 block">Enter Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-3xl font-semibold text-white">₹</span>
                  <Input
                    type="number"
                    placeholder="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-12 text-3xl font-semibold h-16 text-center bg-[#0A0F1F] border-[#586BFF]/30 text-white"
                  />
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment Method</span>
                  <span className="text-white flex items-center gap-1">
                    <PalmNFCIcon className="w-4 h-4" style={{ filter: 'brightness(0) saturate(100%) invert(76%) sepia(86%) saturate(3068%) hue-rotate(154deg) brightness(101%) contrast(101%)' }} />
                    Palm Biometric
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Hash Status</span>
                  <span className="text-green-400">Generated</span>
                </div>
              </div>
            </Card>

            <Button 
              onClick={confirmPayment}
              className="w-full max-w-md bg-gradient-to-r from-[#586BFF] to-[#9B62FF] hover:from-[#4a5ceb] hover:to-[#8a51eb] h-14 text-lg font-semibold"
            >
              Confirm & Pay
            </Button>
          </div>
        </div>
      )}

      {/* Success */}
      {step === 'success' && (
        <div className="min-h-screen flex flex-col items-center justify-center px-6">
          <div className="relative mb-8">
            <div className="absolute inset-0 rounded-full bg-[#64E8FF] blur-3xl opacity-50"></div>
            <div className="relative w-32 h-32 bg-[#64E8FF]/20 rounded-full flex items-center justify-center animate-pulse">
              <CheckCircle2 className="w-16 h-16 text-[#64E8FF]" />
            </div>
          </div>

          <h2 className="text-3xl font-bold mb-2">Payment Successful!</h2>
          <p className="text-gray-300 text-center mb-4">
            ₹{amount} paid to {merchantName}
          </p>
          <p className="text-sm text-[#64E8FF]">Authorized via Palm Biometrics</p>
        </div>
      )}
    </div>
  );
};

export default Palm2QR;